import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Package, Edit2, Check, Printer } from 'lucide-react';
import { Button } from '../Button';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { toast } from 'sonner';
import { Ordem, PedidoDetalhe } from '../../types';
import { ModalImpressao } from '../ModalImpressao';
import './styles.css';
import api from '../../services/api';

interface OrdemDetalhesProps {
  ordem: Ordem;
  onClose: () => void;
  onUpdated?: (updated: Partial<Ordem> & { id: number }) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  em_producao: 'Em produção',
  finalizado: 'Finalizado',
};

export const OrdemDetalhes: React.FC<OrdemDetalhesProps> = ({ ordem, onClose, onUpdated }) => {
  const [pedidos, setPedidos] = useState<PedidoDetalhe[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({});
  const [modelos, setModelos] = useState<any[]>([]);
  const [materiais, setMateriais] = useState<any[]>([]);
  const [showModalImpressao, setShowModalImpressao] = useState(false);

  useEffect(() => {
    // Busca modelos e materiais para exibir nomes
    const fetchData = async () => {
      try {
        const [resModelos, resMateriais] = await Promise.all([
          api.get('/api/v1/modelos/'),
          api.get('/api/v1/materiais/')
        ]);
        setModelos(resModelos.data);
        setMateriais(resMateriais.data);
      } catch (error) {
        console.error('Erro ao buscar modelos e materiais:', error);
      }
    };
    fetchData();
  }, []);

  // Converte pedidos do tipo antigo para o novo se necessário
  useEffect(() => {
    if (ordem.pedidos && ordem.pedidos.length > 0) {
      setPedidos(
        (ordem.pedidos as any[]).map((p: any) => {
          let modeloId = p.modelo ?? '';
          if (typeof modeloId === 'object' && modeloId !== null) modeloId = modeloId.id;
          return {
            id: p.id,
            produto: '', // não existe no backend
            quantidade: p.quantidade,
            material: '', // não existe no backend
            preco_inicial: p.preco_unidade ?? 0,
            preco_final: p.preco_total_pedido ?? 0,
            status: p.status as 'pendente' | 'em_producao' | 'finalizado',
            cor: p.cor ?? '',
            observacao: p.observacao ?? '',
            modelo_nome: modelos.find(m => m.id === Number(modeloId))?.nome || modeloId,
            material_nome: (() => {
              const modelo = modelos.find(m => m.id === Number(modeloId));
              if (!modelo) return '';
              const materialId = typeof modelo.material === 'object' ? modelo.material.id : modelo.material;
              const material = materiais.find(mat => mat.id === materialId);
              return material ? material.nome : '';
            })(),
          };
        })
      );
    }
  }, [ordem.pedidos, modelos, materiais]);

  // Função para alternar status
  const handleStatusChange = async (pedidoId: number, currentStatus: string) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [pedidoId]: true }));
      let newStatus: 'pendente' | 'em_producao' | 'finalizado';
      if (currentStatus === 'pendente') newStatus = 'em_producao';
      else if (currentStatus === 'em_producao') newStatus = 'finalizado';
      else newStatus = 'pendente';
      
      await api.patch(`/api/v1/pedidos/${pedidoId}/`, { status: newStatus });
      
      // Atualiza os pedidos e verifica se todos estão finalizados
      let shouldFinalizeOrdem = false;
      const updatedPedidos = pedidos.map(p => {
        if (p.id === pedidoId) {
          return { ...p, status: newStatus };
        }
        return p;
      });
      
      // Verifica se todos os pedidos estão finalizados
      const allFinalized = updatedPedidos.length > 0 && updatedPedidos.every(p => p.status === 'finalizado');
      
      if (allFinalized) {
        shouldFinalizeOrdem = true;
      }
      
      // Atualiza o estado dos pedidos
      setPedidos(updatedPedidos);
      
      if (shouldFinalizeOrdem) {
        // Finaliza a ordem de serviço
        try {
          await api.patch(`/api/v1/ordens_servico/${ordem.id}/`, { 
            status: 'finalizado',
            finalizado_em: new Date().toISOString()
          });
          toast.success('Ordem finalizada automaticamente!');
          onUpdated && onUpdated({ id: ordem.id, status: 'finalizado', finalizado_em: new Date().toISOString() });
        } catch (error) {
          console.error('Erro ao finalizar ordem:', error);
        }
      } else {
        toast.success('Status atualizado com sucesso!');
        // Se ainda não finalizou a ordem, garante que o status da ordem acompanhe (se necessário)
        // Aqui não mudamos status da ordem diretamente; apenas notifica possíveis mudanças futuras
        onUpdated && onUpdated({ id: ordem.id });
      }

    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [pedidoId]: false }));
    }
  };

  return (
    <div className="ordem-detalhes-overlay">
      <div className="ordem-detalhes-container">
        <div className="ordem-detalhes-header">
          <div className="ordem-detalhes-header-content">
            <h2 className="ordem-detalhes-title">
              Ordem de Serviço #{ordem.id}
            </h2>
            <div className="ordem-detalhes-meta">
              <div className="ordem-detalhes-meta-item">
                <Calendar className="icon" />
                {formatDate(ordem.data)}
              </div>
              <div className="ordem-detalhes-meta-item">
                <Package className="icon" />
                {pedidos.length} {pedidos.length === 1 ? 'pedido' : 'pedidos'}
              </div>
              <div className="ordem-detalhes-meta-item ordem-detalhes-total">
                <DollarSign className="icon" />
                {formatCurrency(ordem.valor_total)}
              </div>
            </div>
          </div>
          <div className="ordem-detalhes-header-actions">
            <Button
              data-testid="print-ordem-button"
              onClick={() => setShowModalImpressao(true)}
              variant="outline"
              size="sm"
              className="print-button"
            >
              <Printer className="icon-small" />
              Imprimir
            </Button>
            <Button
              data-testid="close-detalhes-button"
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="close-button"
            >
              <X className="icon-close" />
            </Button>
          </div>
        </div>

        <div className="ordem-detalhes-content">
          <h3 className="ordem-detalhes-content-title">
            Pedidos
          </h3>
          
          {pedidos.length === 0 ? (
            <p className="ordem-detalhes-empty">Nenhum pedido nesta ordem</p>
          ) : (
            <div className="ordem-detalhes-pedidos">
              {pedidos.map((pedido) => (
                <div
                  key={pedido.id}
                  data-testid={`pedido-item-${pedido.id}`}
                  className="ordem-detalhes-pedido"
                >
                  <div className="ordem-detalhes-pedido-grid">
                    <div className="ordem-detalhes-pedido-left">
                      <div className="ordem-detalhes-field">
                        <p className="ordem-detalhes-field-label">Modelo</p>
                        <p className="ordem-detalhes-field-value">{pedido.modelo_nome}</p>
                      </div>
                      
                      <div className="ordem-detalhes-field-grid">
                        <div className="ordem-detalhes-field">
                          <p className="ordem-detalhes-field-label">Quantidade</p>
                          <p className="ordem-detalhes-field-value">{pedido.quantidade}x</p>
                        </div>
                        <div className="ordem-detalhes-field">
                          <p className="ordem-detalhes-field-label">Material</p>
                          <p className="ordem-detalhes-field-value">{pedido.material_nome}</p>
                        </div>
                        <div className="ordem-detalhes-field">
                          <p className="ordem-detalhes-field-label">Cor</p>
                          <p className="ordem-detalhes-field-value">{pedido.cor}</p>
                        </div>
                      </div>
                    </div>

                    <div className="ordem-detalhes-pedido-right">
                      <div className="ordem-detalhes-field-grid">
                        <div className="ordem-detalhes-field">
                          <p className="ordem-detalhes-field-label">Preço Inicial</p>
                          <p className="ordem-detalhes-field-value">{formatCurrency(pedido.preco_inicial)}</p>
                        </div>
                        <div className="ordem-detalhes-field">
                          <p className="ordem-detalhes-field-label">Preço Final</p>
                          <p className="ordem-detalhes-field-value ordem-detalhes-field-value-highlight">
                            {formatCurrency(pedido.preco_final)}
                          </p>
                        </div>
                      </div>

                      <div className="ordem-detalhes-status-container">
                        <div className="ordem-detalhes-status-content">
                          <p className="ordem-detalhes-field-label">Status</p>
                          <span
                            data-testid={`pedido-status-${pedido.id}`}
                            className={`status-badge status-${pedido.status}`}
                          >
                            {pedido.status === 'finalizado' && <Check className="icon-small" />}
                            {STATUS_LABELS[pedido.status]}
                          </span>
                        </div>
                        {pedido.observacao && (
                          <div className="ordem-detalhes-field" style={{ marginTop: 8 }}>
                            <p className="ordem-detalhes-field-label">Observação</p>
                            <p className="ordem-detalhes-field-value">{pedido.observacao}</p>
                          </div>
                        )}
                        
                        <Button
                          data-testid={`toggle-status-button-${pedido.id}`}
                          onClick={() => handleStatusChange(pedido.id, pedido.status)}
                          disabled={updatingStatus[pedido.id]}
                          size="sm"
                          variant="outline"
                          className="toggle-button"
                        >
                          <Edit2 className="icon-small" />
                          {pedido.status === 'pendente' ? 'Produção' : pedido.status === 'em_producao' ? 'Finalizar' : 'Reabrir'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModalImpressao && (
        <ModalImpressao
          ordem={ordem}
          pedidos={pedidos}
          onClose={() => setShowModalImpressao(false)}
        />
      )}
    </div>
  );
};

